import { useState, memo, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  ComposableMap,
  Geographies,
  Geography,
  ZoomableGroup,
  Marker,
} from 'react-simple-maps';
import { RefreshCw } from 'lucide-react';
import {
  getStatus,
  getStatusColor,
  getOpportunityStatus,
  getOpportunityStatusColor,
  type ViewMode,
  type PressureStatus,
} from '@/data/regulatoryData';
import { fetchLatestScores, createScoreMap, type CountryScore, type ScoresData } from '@/services/scoreService';
import { useKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts';
import CountryTooltip from './CountryTooltip';
import ScoreExplainer from '@/components/ScoreExplainer';
import ZoomControls from './ZoomControls';
import HelpModal from '@/components/HelpModal';
import CommandPalette from '@/components/CommandPalette';
import ViewModeToggle from './ViewModeToggle';
import Legend from './Legend';
import TrajectoryOverlay from './TrajectoryOverlay';
import SiteBranding from './SiteBranding';
import InlineSearch, { type InlineSearchHandle } from './InlineSearch';
import StatusFilter from './StatusFilter';

// GeoJSON URLs for different geographic layers
const worldGeoUrl = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';
const usStatesGeoUrl = 'https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json';
const canadaProvincesGeoUrl = 'https://gist.githubusercontent.com/Brideau/2391df60938462571ca9/raw/f5a1f3b47ff671eaf2fb7e7b798bacfc6962606a/canadaprovtopo.json';

// Country code mapping from Natural Earth/World Atlas to our data codes (ISO alpha-2)
// Includes all 71 jurisdictions (US states and CA provinces scored independently)
const countryNameMap: Record<string, string> = {
  // Original 10 countries (excluding US/CA which have sub-national data)
  'United Kingdom': 'GB',
  'Malta': 'MT',
  'Germany': 'DE',
  'Netherlands': 'NL',
  'Sweden': 'SE',
  'Spain': 'ES',
  'Italy': 'IT',
  'Portugal': 'PT',

  // New EU countries (Phase 5)
  'France': 'FR',
  'Austria': 'AT',
  'Belgium': 'BE',
  'Greece': 'GR',
  'Denmark': 'DK',
  'Ireland': 'IE',
  'Poland': 'PL',
  'Czech Republic': 'CZ',
  'Czechia': 'CZ',
  'Hungary': 'HU',
  'Romania': 'RO',
  'Bulgaria': 'BG',
  'Croatia': 'HR',
  'Finland': 'FI',
  'Norway': 'NO',
  'Switzerland': 'CH',

  // APAC (Phase 5)
  'Australia': 'AU',
  'New Zealand': 'NZ',
  'Japan': 'JP',
  'South Korea': 'KR',
  'Korea': 'KR',
  'Macau': 'MO',
  'Singapore': 'SG',
  'Philippines': 'PH',
  'India': 'IN',

  // LATAM (Phase 5)
  'Colombia': 'CO',
  'Mexico': 'MX',
  'Brazil': 'BR',
  'Argentina': 'AR',
  'Chile': 'CL',
  'Peru': 'PE',
  'Panama': 'PA',
  'Costa Rica': 'CR',

  // Africa & Middle East (Phase 5)
  'South Africa': 'ZA',
  'Kenya': 'KE',
  'Nigeria': 'NG',
  'United Arab Emirates': 'AE',

  // Additional aliases for GeoJSON variations
  'Republic of South Africa': 'ZA',
  'RSA': 'ZA',
  'Republic of Korea': 'KR',
  'Korea, Republic of': 'KR',
  'U.A.E.': 'AE',
  'UAE': 'AE',
  'Holland': 'NL',
  'The Netherlands': 'NL',
  'Great Britain': 'GB',
  'UK': 'GB',
  'Macao': 'MO',
  'Republic of Ireland': 'IE',
  'Eire': 'IE',
};

// US state name to code mapping (25 states with legal gambling)
const usStateNameMap: Record<string, string> = {
  'Nevada': 'US-NV',
  'New Jersey': 'US-NJ',
  'Pennsylvania': 'US-PA',
  'Michigan': 'US-MI',
  'West Virginia': 'US-WV',
  'Connecticut': 'US-CT',
  'Indiana': 'US-IN',
  'Iowa': 'US-IA',
  'Colorado': 'US-CO',
  'Arizona': 'US-AZ',
  'Virginia': 'US-VA',
  'Maryland': 'US-MD',
  'New York': 'US-NY',
  'Ohio': 'US-OH',
  'Massachusetts': 'US-MA',
  'Illinois': 'US-IL',
  'Louisiana': 'US-LA',
  'Mississippi': 'US-MS',
  'Tennessee': 'US-TN',
  'Kentucky': 'US-KY',
  'North Carolina': 'US-NC',
  'Florida': 'US-FL',
  'Kansas': 'US-KS',
  'New Hampshire': 'US-NH',
  'Rhode Island': 'US-RI',
};

// Canadian province name to code mapping
const canadaProvinceNameMap: Record<string, string> = {
  // Provinces with data
  'Ontario': 'CA-ON',
  'British Columbia': 'CA-BC',
  'Alberta': 'CA-AB',
  'Quebec': 'CA-QC',
  'Québec': 'CA-QC',
  'Manitoba': 'CA-MB',
  'Saskatchewan': 'CA-SK',
  'Nova Scotia': 'CA-NS',
  'New Brunswick': 'CA-NB',
  'Newfoundland and Labrador': 'CA-NL',
  'Prince Edward Island': 'CA-PE',
  // Territories (fallback to national CA data)
  'Yukon': 'CA',
  'Northwest Territories': 'CA',
  'Nunavut': 'CA',
};

// Small countries that need marker points (too small to see on world map)
const smallCountryMarkers: Array<{ code: string; coordinates: [number, number]; name: string }> = [
  { code: 'MT', coordinates: [14.5, 35.9], name: 'Malta' },
  { code: 'SG', coordinates: [103.8, 1.35], name: 'Singapore' },
  { code: 'MO', coordinates: [113.55, 22.2], name: 'Macau' },
];

const WorldMap = () => {
  const [hoveredCountry, setHoveredCountry] = useState<CountryScore | null>(null);
  const [selectedCountry, setSelectedCountry] = useState<CountryScore | null>(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [scores, setScores] = useState<ScoresData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [center, setCenter] = useState<[number, number]>([15, 40]);
  const [showHelp, setShowHelp] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('pressure');
  const [showTrajectory, setShowTrajectory] = useState(false); // Default off to reduce clutter
  const [activeFilters, setActiveFilters] = useState<Set<PressureStatus>>(
    new Set(['stable', 'watch', 'tightening', 'high'])
  );
  const inlineSearchRef = useRef<InlineSearchHandle>(null);

  // Fetch scores on mount
  useEffect(() => {
    const loadScores = async () => {
      try {
        const data = await fetchLatestScores();
        setScores(data);
        setLoading(false);
      } catch (err) {
        console.error('Failed to fetch scores:', err);
        setError(err instanceof Error ? err.message : 'Failed to load data');
        setLoading(false);
      }
    };

    loadScores();
  }, []);

  // Create score lookup map
  const scoreMap = useMemo(() => {
    if (!scores) return {};
    return createScoreMap(scores);
  }, [scores]);

  // Get jurisdiction code from geo properties (works for countries, states, and provinces)
  // Falls back to national data (US/CA) for unmapped states/provinces/territories
  const getJurisdictionCode = useCallback((geo: any, type: 'country' | 'us-state' | 'ca-province' = 'country') => {
    const name = geo.properties.name || geo.properties.NAME;

    switch (type) {
      case 'us-state': {
        const stateCode = usStateNameMap[name];
        return stateCode || null;
      }
      case 'ca-province': {
        const provinceCode = canadaProvinceNameMap[name];
        return provinceCode || null;
      }
      default:
        return countryNameMap[name] || null;
    }
  }, []);

  const handleMouseEnter = useCallback((geo: any, event: React.MouseEvent, type: 'country' | 'us-state' | 'ca-province' = 'country') => {
    const code = getJurisdictionCode(geo, type);
    const data = code ? scoreMap[code] : null;

    if (data) {
      setHoveredCountry(data);
      setMousePosition({ x: event.clientX, y: event.clientY });
    }
  }, [scoreMap, getJurisdictionCode]);

  const handleMouseMove = useCallback((event: React.MouseEvent) => {
    if (hoveredCountry) {
      setMousePosition({ x: event.clientX, y: event.clientY });
    }
  }, [hoveredCountry]);

  const handleMouseLeave = useCallback(() => {
    setHoveredCountry(null);
  }, []);

  const handleCountryClick = useCallback((geo: any, type: 'country' | 'us-state' | 'ca-province' = 'country') => {
    const code = getJurisdictionCode(geo, type);
    const data = code ? scoreMap[code] : null;

    if (data) {
      setSelectedCountry(data);
      setHoveredCountry(null); // Hide tooltip when showing panel
    }
  }, [scoreMap, getJurisdictionCode]);

  const handleCloseExplainer = useCallback(() => {
    setSelectedCountry(null);
  }, []);

  const getRegionFill = useCallback((geo: any, type: 'country' | 'us-state' | 'ca-province' = 'country') => {
    const code = getJurisdictionCode(geo, type);
    const data = code ? scoreMap[code] : null;

    if (!data) {
      return '#1a1a1a'; // Very dark grey for regions without data
    }

    // Get the status for filtering
    const status = getStatus(data.score);

    // Check if status is filtered out
    if (!activeFilters.has(status)) {
      return '#1a1a1a20'; // Very dim for filtered-out regions
    }

    // Use different color schemes based on view mode
    if (viewMode === 'opportunity') {
      // For opportunity view, use opportunity_score if available, otherwise calculate from pressure
      const opportunityScore = data.opportunity_score ?? (100 - data.score);
      const oppStatus = getOpportunityStatus(opportunityScore);
      return getOpportunityStatusColor(oppStatus);
    }

    // Default: pressure view
    return getStatusColor(status);
  }, [scoreMap, getJurisdictionCode, viewMode, activeFilters]);

  // Check if a region has data (for cursor styling)
  const hasData = useCallback((geo: any, type: 'country' | 'us-state' | 'ca-province' = 'country') => {
    const code = getJurisdictionCode(geo, type);
    return code && scoreMap[code];
  }, [scoreMap, getJurisdictionCode]);

  const refreshData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchLatestScores();
      setScores(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to refresh data');
    } finally {
      setLoading(false);
    }
  }, []);

  // Zoom controls
  const handleZoomIn = useCallback(() => {
    setZoom((z) => Math.min(z + 0.5, 8));
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoom((z) => Math.max(z - 0.5, 1));
  }, []);

  const handleZoomReset = useCallback(() => {
    setZoom(1);
    setCenter([15, 40]);
  }, []);

  const handleMoveEnd = useCallback((position: { coordinates: [number, number]; zoom: number }) => {
    setCenter(position.coordinates);
    setZoom(position.zoom);
  }, []);

  // Open help modal
  const handleOpenHelp = useCallback(() => {
    setShowHelp(true);
  }, []);

  const handleCloseHelp = useCallback(() => {
    setShowHelp(false);
  }, []);

  // Open search (modal - legacy, kept for compatibility)
  const handleOpenSearch = useCallback(() => {
    setShowSearch(true);
  }, []);

  const handleCloseSearch = useCallback(() => {
    setShowSearch(false);
  }, []);

  // Focus inline search
  const handleFocusInlineSearch = useCallback(() => {
    inlineSearchRef.current?.focus();
  }, []);

  // Handle jurisdiction selection from search
  const handleSearchSelect = useCallback((jurisdiction: CountryScore) => {
    setSelectedCountry(jurisdiction);
    setShowSearch(false);
  }, []);

  // Handle filter change
  const handleFilterChange = useCallback((filters: Set<PressureStatus>) => {
    setActiveFilters(filters);
  }, []);

  // Toggle view mode (pressure <-> opportunity)
  const handleToggleViewMode = useCallback(() => {
    setViewMode((mode) => mode === 'pressure' ? 'opportunity' : 'pressure');
  }, []);

  // Toggle trajectory arrows visibility
  const handleToggleTrajectory = useCallback(() => {
    setShowTrajectory((show) => !show);
  }, []);

  // Get all jurisdictions as array for search
  const allJurisdictions = useMemo(() => {
    return Object.values(scoreMap);
  }, [scoreMap]);

  // Keyboard shortcuts
  useKeyboardShortcuts([
    { key: 'Escape', action: handleCloseExplainer, description: 'Close panel' },
    { key: '+', action: handleZoomIn, description: 'Zoom in' },
    { key: '=', action: handleZoomIn, description: 'Zoom in' },
    { key: '-', action: handleZoomOut, description: 'Zoom out' },
    { key: '0', action: handleZoomReset, description: 'Reset zoom' },
    { key: '/', action: handleFocusInlineSearch, description: 'Focus search' },
    { key: '?', shift: true, action: handleOpenHelp, description: 'Show help' },
    { key: 'v', action: handleToggleViewMode, description: 'Toggle view mode' },
    { key: 't', action: handleToggleTrajectory, description: 'Toggle trajectory arrows' },
    { key: 'f', action: () => {}, description: 'Toggle filter' }, // Filter handled by dropdown
  ], !showSearch && !showHelp);

  // Loading state with beautiful skeleton
  if (loading && !scores) {
    return (
      <div className="absolute inset-0 flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-6">
          {/* Animated globe skeleton */}
          <div className="relative w-32 h-32">
            <div className="absolute inset-0 rounded-full bg-gradient-to-br from-muted to-card animate-globe-pulse" />
            <div className="absolute inset-4 rounded-full bg-background" />
            <div className="absolute top-1/2 left-0 right-0 h-px bg-muted/50" />
            <div className="absolute left-1/2 top-0 bottom-0 w-px bg-muted/50" />
            {/* Orbit ring */}
            <div className="absolute inset-[-8px] rounded-full border border-dashed border-muted/30 animate-spin" style={{ animationDuration: '8s' }} />
          </div>

          {/* Shimmer text */}
          <div className="relative overflow-hidden">
            <span className="text-text-secondary text-sm">Loading regulatory data</span>
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-shimmer" />
          </div>

          {/* Progress dots */}
          <div className="flex gap-1.5">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="w-2 h-2 rounded-full bg-brand"
                style={{
                  animation: 'bounce-subtle 1s ease-in-out infinite',
                  animationDelay: `${i * 0.15}s`,
                }}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Error state with glass styling
  if (error) {
    return (
      <div className="absolute inset-0 flex items-center justify-center bg-background">
        <div className="glass rounded-xl p-8 max-w-sm text-center shadow-card-elevated">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-destructive/10 flex items-center justify-center">
            <svg className="h-8 w-8 text-destructive" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-text-primary mb-2">
            Unable to Load Data
          </h3>
          <p className="text-sm text-text-secondary mb-6">
            {error}
          </p>
          <button
            onClick={refreshData}
            className="inline-flex items-center gap-2 px-4 py-2 bg-brand hover:bg-brand/80 text-white rounded-lg transition-colors"
          >
            <RefreshCw className="h-4 w-4" />
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="absolute inset-0"
      style={{ backgroundColor: '#0f0f0f' }}
      onMouseMove={handleMouseMove}
    >
      {/* Site Branding - Top Left */}
      <SiteBranding />

      {/* Top Right Toolbar: Search + Filter */}
      <div className="fixed top-6 right-6 z-[1000] flex items-center gap-3">
        <InlineSearch
          ref={inlineSearchRef}
          jurisdictions={allJurisdictions}
          onSelect={handleSearchSelect}
        />
        <StatusFilter
          activeFilters={activeFilters}
          onFilterChange={handleFilterChange}
          viewMode={viewMode}
        />
      </div>

      {/* Left Controls: View Mode + Trajectory */}
      <div className="fixed top-[90px] left-6 z-[1000] flex flex-col gap-2">
        <ViewModeToggle viewMode={viewMode} onViewModeChange={setViewMode} />
        <button
          onClick={handleToggleTrajectory}
          className={`glass rounded-lg shadow-glass h-9 px-3
                     flex items-center gap-2 text-xs transition-all duration-200
                     ${showTrajectory
                       ? 'text-text-primary bg-white/10 border border-white/20'
                       : 'text-text-secondary hover:text-text-primary hover:bg-white/5'
                     }`}
          title="Toggle trajectory arrows (T)"
        >
          <span className={`text-sm ${showTrajectory ? 'opacity-100' : 'opacity-50'}`}>
            {showTrajectory ? '↗' : '○'}
          </span>
          <span className="font-medium">Trajectory</span>
          <kbd className="ml-auto px-1.5 py-0.5 rounded bg-white/10 text-text-tertiary text-[10px]">T</kbd>
        </button>
      </div>

      <ComposableMap
        projection="geoMercator"
        projectionConfig={{
          scale: 160,
          center: [15, 40],
        }}
        style={{
          width: '100%',
          height: '100%',
        }}
      >
        <ZoomableGroup
          center={center}
          zoom={zoom}
          minZoom={1}
          maxZoom={8}
          onMoveEnd={handleMoveEnd}
        >
          {/* Base world map (excluding US and Canada which have state/province data) */}
          <Geographies geography={worldGeoUrl}>
            {({ geographies }) =>
              geographies
                .filter((geo) => {
                  const name = geo.properties.name;
                  // Skip US and Canada as we'll render their states/provinces separately
                  return name !== 'United States of America' && name !== 'Canada';
                })
                .map((geo) => {
                  const regionHasData = hasData(geo, 'country');
                  return (
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      fill={getRegionFill(geo, 'country')}
                      stroke="#252525"
                      strokeWidth={0.4}
                      style={{
                        default: {
                          outline: 'none',
                          cursor: regionHasData ? 'pointer' : 'default',
                          transition: 'all 0.2s ease-out',
                        },
                        hover: {
                          outline: 'none',
                          fill: getRegionFill(geo, 'country'),
                          filter: regionHasData ? 'brightness(1.3)' : 'brightness(1)',
                          stroke: regionHasData ? '#ffffff' : '#252525',
                          strokeWidth: regionHasData ? 1.5 : 0.4,
                          cursor: regionHasData ? 'pointer' : 'default',
                        },
                        pressed: {
                          outline: 'none',
                          filter: 'brightness(1.1)',
                        },
                      }}
                      onMouseEnter={(e) => handleMouseEnter(geo, e, 'country')}
                      onMouseLeave={handleMouseLeave}
                      onClick={() => handleCountryClick(geo, 'country')}
                    />
                  );
                })
            }
          </Geographies>

          {/* US States layer */}
          <Geographies geography={usStatesGeoUrl}>
            {({ geographies }) =>
              geographies.map((geo) => {
                const regionHasData = hasData(geo, 'us-state');
                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    fill={getRegionFill(geo, 'us-state')}
                    stroke="#303030"
                    strokeWidth={0.3}
                    style={{
                      default: {
                        outline: 'none',
                        cursor: regionHasData ? 'pointer' : 'default',
                        transition: 'all 0.2s ease-out',
                      },
                      hover: {
                        outline: 'none',
                        fill: getRegionFill(geo, 'us-state'),
                        filter: regionHasData ? 'brightness(1.3)' : 'brightness(1)',
                        stroke: regionHasData ? '#ffffff' : '#303030',
                        strokeWidth: regionHasData ? 1 : 0.3,
                        cursor: regionHasData ? 'pointer' : 'default',
                      },
                      pressed: {
                        outline: 'none',
                        filter: 'brightness(1.1)',
                      },
                    }}
                    onMouseEnter={(e) => handleMouseEnter(geo, e, 'us-state')}
                    onMouseLeave={handleMouseLeave}
                    onClick={() => handleCountryClick(geo, 'us-state')}
                  />
                );
              })
            }
          </Geographies>

          {/* Canadian Provinces layer */}
          <Geographies geography={canadaProvincesGeoUrl}>
            {({ geographies }) =>
              geographies.map((geo) => {
                const regionHasData = hasData(geo, 'ca-province');
                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    fill={getRegionFill(geo, 'ca-province')}
                    stroke="#303030"
                    strokeWidth={0.3}
                    style={{
                      default: {
                        outline: 'none',
                        cursor: regionHasData ? 'pointer' : 'default',
                        transition: 'all 0.2s ease-out',
                      },
                      hover: {
                        outline: 'none',
                        fill: getRegionFill(geo, 'ca-province'),
                        filter: regionHasData ? 'brightness(1.3)' : 'brightness(1)',
                        stroke: regionHasData ? '#ffffff' : '#303030',
                        strokeWidth: regionHasData ? 1 : 0.3,
                        cursor: regionHasData ? 'pointer' : 'default',
                      },
                      pressed: {
                        outline: 'none',
                        filter: 'brightness(1.1)',
                      },
                    }}
                    onMouseEnter={(e) => handleMouseEnter(geo, e, 'ca-province')}
                    onMouseLeave={handleMouseLeave}
                    onClick={() => handleCountryClick(geo, 'ca-province')}
                  />
                );
              })
            }
          </Geographies>

          {/* Small Country Markers - for countries too small to see on the map */}
          {smallCountryMarkers.map((marker) => {
            const data = scoreMap[marker.code];
            if (!data) return null;

            const status = getStatus(data.score);
            if (!activeFilters.has(status)) return null;

            const fillColor = viewMode === 'opportunity'
              ? getOpportunityStatusColor(getOpportunityStatus(data.opportunity_score ?? (100 - data.score)))
              : getStatusColor(status);

            const isHovered = hoveredCountry?.code === marker.code;

            return (
              <Marker key={marker.code} coordinates={marker.coordinates}>
                <g
                  style={{ cursor: 'pointer' }}
                  onMouseEnter={(e) => {
                    setHoveredCountry(data);
                    setMousePosition({ x: e.clientX, y: e.clientY });
                  }}
                  onMouseLeave={handleMouseLeave}
                  onClick={() => {
                    setSelectedCountry(data);
                    setHoveredCountry(null);
                  }}
                >
                  {/* Outer glow ring */}
                  <circle
                    r={5}
                    fill={fillColor}
                    fillOpacity={0.3}
                    className="animate-pulse"
                  />
                  {/* Main marker */}
                  <circle
                    r={3}
                    fill={fillColor}
                    stroke={isHovered ? '#ffffff' : fillColor}
                    strokeWidth={isHovered ? 1 : 0.5}
                    style={{
                      filter: isHovered ? 'brightness(1.3)' : 'none',
                      transition: 'all 0.15s ease-out',
                    }}
                  />
                </g>
              </Marker>
            );
          })}

          {/* Trajectory Overlay - shows directional arrows */}
          <TrajectoryOverlay
            jurisdictions={allJurisdictions}
            visible={showTrajectory}
          />
        </ZoomableGroup>
      </ComposableMap>

      {/* Zoom Controls */}
      <ZoomControls
        zoom={zoom}
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        onReset={handleZoomReset}
      />

      {hoveredCountry && !selectedCountry && (
        <CountryTooltip country={hoveredCountry} position={mousePosition} lastUpdated={scores?.last_updated || ''} />
      )}

      {selectedCountry && (
        <ScoreExplainer
          jurisdiction={selectedCountry}
          onClose={handleCloseExplainer}
        />
      )}

      {/* Help Modal */}
      <HelpModal isOpen={showHelp} onClose={handleCloseHelp} />

      {/* Command Palette / Search */}
      <CommandPalette
        isOpen={showSearch}
        onClose={handleCloseSearch}
        jurisdictions={allJurisdictions}
        onSelect={handleSearchSelect}
      />

      {/* Legend (dynamic based on view mode) */}
      <Legend viewMode={viewMode} />
    </div>
  );
};

export default memo(WorldMap);
